import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownIcon, ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
import { solutionPath } from '../../data/solutions';
import type { Solution } from '../../types/platform';
import { Reveal } from '../ui/Reveal';
import { cn } from '../../src/lib/utils';
const FLOW_KEYS = [{
  key: 'input',
  label: 'Input'
}, {
  key: 'agents',
  label: 'Agents'
}, {
  key: 'workflow',
  label: 'Workflow'
}, {
  key: 'decision',
  label: 'Decision'
}, {
  key: 'monitoring',
  label: 'Monitoring'
}] as const;
export function SolutionBlock({
  solution,
  index
}: {
  solution: Solution;
  index: number;
}) {
  const [open, setOpen] = useState(false);
  const panelId = `${solution.id}-flow`;
  return <section id={solution.id} aria-labelledby={`${solution.id}-heading`} className="scroll-mt-40 rounded-3xl border border-hairline bg-panel p-6 sm:p-8 lg:p-10" data-id="element-966" data-mp="3hzblveljpmta">
      <Reveal className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-12" data-id="element-967" data-mp="26lzn3i4n7udz">
        <div data-id="element-968" data-mp="1vj7y07vr6vw0">
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-lime" data-id="element-969" data-mp="1vl2jpoeums15">
            {String(index + 1).padStart(2, '0')} · {solution.label}
          </p>
          <h2 id={`${solution.id}-heading`} className="sf-display mt-3 text-balance text-[28px] leading-[1.08] text-text-primary sm:text-[36px]" data-id="element-970" data-mp="24kfwapy9rwq3">
            {solution.headline}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-text-secondary sm:text-base" data-id="element-971" data-mp="2eivm8plk8zy0">{solution.line}</p>
          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-tertiary" data-id="element-972" data-mp="29k26srmpqyv7">{solution.tagsLabel}</p>
          <ul className="mt-2 flex flex-wrap gap-1.5" data-id="element-973" data-mp="2o7n9wljl8y34">
            {solution.tags.map(tag => <li key={tag} className="rounded-md border border-hairline-soft bg-surface px-2.5 py-1 text-[13px] text-text-secondary" data-id="element-974" data-mp="2jan66ha7o7f6">
                {tag}
              </li>)}
          </ul>
          <Link to={solutionPath(solution.id)} className="mt-6 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-lime px-5 text-sm font-semibold text-white transition-colors hover:bg-[#2B1E7A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2" data-id="element-975" data-mp="2sbbmxjqzolvr">
            Explore {solution.label}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-976" data-mp="23osgg125kij7" />
          </Link>
          <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls={panelId} className="mt-2 flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-lime focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime lg:hidden" data-id="element-977" data-mp="28nlx22cqkpnl">
            {open ? 'Hide the process' : 'Show the process'}
            <ChevronDownIcon className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden="true" data-id="element-978" data-mp="o8a4se3mu0og" />
          </button>
        </div>

        <ol id={panelId} aria-label={`${solution.headline} process`} className={cn('flex-col', open ? 'flex' : 'hidden', 'lg:flex')} data-id="element-979" data-mp="sax573edctx9">
          {FLOW_KEYS.map((step, stepIndex) => {
          const value = solution.flow[step.key];
          return <li key={step.key} className="flex flex-col" data-id="element-980" data-mp="b0kuc790ue8e" data-mpi={stepIndex}>
                <div className={cn('grid gap-2 rounded-xl border px-4 py-3 sm:grid-cols-[110px_minmax(0,1fr)] sm:items-center', step.key === 'decision' ? 'border-lime/30 bg-violet-soft' : 'border-hairline bg-surface')} data-id="element-981" data-mp="6yg29cy81pnv">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-tertiary" data-id="element-982" data-mp="umzbbup3i6h0">{step.label}</span>
                  {Array.isArray(value) ? <ul className="flex flex-wrap gap-1.5" data-id="element-983" data-mp="3fdzty7j8v00t">
                      {value.map(agent => <li key={agent} className="rounded-md bg-lime px-2 py-0.5 text-[13px] font-medium text-white" data-id="element-984" data-mp="3af6bs1t0ikub">
                          {agent}
                        </li>)}
                    </ul> : <span className={cn('text-sm', step.key === 'decision' ? 'font-semibold text-lime' : 'text-text-primary')} data-id="element-985" data-mp="16yhzt10gs4n8">{value}</span>}
                </div>
                {stepIndex < FLOW_KEYS.length - 1 ? <span className="flex h-5 items-center justify-center text-text-tertiary" aria-hidden="true" data-id="element-986" data-mp="1kwollis52e6j">
                    <ArrowDownIcon className="h-3.5 w-3.5" data-id="element-987" data-mp="1pwb0dwnm82hg" />
                  </span> : null}
              </li>;
        })}
        </ol>
      </Reveal>
    </section>;
}